"use client";

import React from "react";
import { Phone, ArrowRight, CircleCheck as CheckCircle2, ShieldCheck, Star, Clock, Lock, Loader as Loader2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
const HERO_IMG = {
  desktop: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1681701/img/technician-garage-door-repair-768.webp",
  tablet: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1681701/img/technician-garage-door-repair-768.webp",
  mobile: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1681701/img/technician-garage-door-repair-768.webp"
};
const NAVY = "#081438";
const GOLD = "#F5A623";
const FONT_HEAD = "'Plus Jakarta Sans', system-ui, sans-serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";
function HeroQuoteForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-10 text-center space-y-4 animate-in fade-in zoom-in duration-300">
        <div className="size-16 bg-green-100 rounded-lg flex items-center justify-center">
          <CheckCircle2 className="size-10 text-green-600" aria-hidden="true" />
        </div>
        <div className="space-y-2">
          <h3 className="text-xl font-bold text-[#081438]">Request Received!</h3>
          <p className="text-gray-600 max-w-[280px] mx-auto">
            {successMessage || "Thank you! Your quote request has been sent. We'll contact you shortly."}
          </p>
        </div>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="space-y-4">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem className="space-y-1.5">
            <FormLabel className="text-sm font-bold text-[#081438]/90">Name</FormLabel>
            <FormControl>
              <Input placeholder="Your full name" aria-label="Name" className="bg-gray-50 border-gray-200 focus:border-[#F5A623] focus:ring-[#F5A623]/20 h-11 rounded-xl transition-all" {...field} />
            </FormControl>
            <FormMessage className="text-xs" />
          </FormItem>} />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField control={control} name="phone" rules={{
          required: "Phone is required",
          pattern: {
            value: /^[0-9+\s-]{8,15}$/,
            message: "Invalid phone number"
          }
        }} render={({
          field
        }) => <FormItem className="space-y-1.5">
              <FormLabel className="text-sm font-bold text-[#081438]/90">Phone</FormLabel>
              <FormControl>
                <Input placeholder="0400 000 000" aria-label="Phone" className="bg-gray-50 border-gray-200 focus:border-[#F5A623] focus:ring-[#F5A623]/20 h-11 rounded-xl transition-all" {...field} />
              </FormControl>
              <FormMessage className="text-xs" />
            </FormItem>} />

          <FormField control={control} name="suburb" rules={{
          required: "Suburb is required"
        }} render={({
          field
        }) => <FormItem className="space-y-1.5">
              <FormLabel className="text-sm font-bold text-[#081438]/90">Suburb</FormLabel>
              <FormControl>
                <Input placeholder="e.g. Parramatta" aria-label="Suburb" className="bg-gray-50 border-gray-200 focus:border-[#F5A623] focus:ring-[#F5A623]/20 h-11 rounded-xl transition-all" {...field} />
              </FormControl>
              <FormMessage className="text-xs" />
            </FormItem>} />
        </div>

        <FormField control={control} name="service" rules={{
        required: "Please select a service"
      }} render={({
        field
      }) => <FormItem className="space-y-1.5">
            <FormLabel className="text-sm font-bold text-[#081438]/90">Service Needed</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger aria-label="Select service needed" className="bg-gray-50 border-gray-200 focus:border-[#F5A623] focus:ring-[#F5A623]/20 h-11 rounded-xl transition-all">
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="emergency-garage-door-repair">Emergency Garage Door Repair</SelectItem>
                <SelectItem value="new-garage-door-installation">New Garage Door Installation</SelectItem>
                <SelectItem value="spring-cable-replacement">Spring &amp; Cable Replacement</SelectItem>
                <SelectItem value="motor-opener-repair">Motor &amp; Opener Repair</SelectItem>
                <SelectItem value="remote-sensor-issues">Remote &amp; Sensor Issues</SelectItem>
                <SelectItem value="safety-inspection">Safety Inspection</SelectItem>
                <SelectItem value="other-issue">Other Issue</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage className="text-xs" />
          </FormItem>} />

        {submitError && <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-xs font-medium">
            {submitError}
          </div>}

        <Button type="submit" disabled={isSubmitting} aria-label="Submit quote request" className="w-full bg-[#F5A623] hover:bg-[#D4A63A] text-[#081438] font-bold py-6 text-base rounded-xl shadow-lg shadow-[#F5A623]/30 transition-all active:scale-[0.98]">
          {isSubmitting ? <>
              <Loader2 className="mr-2 h-5 w-5 animate-spin" aria-hidden="true" />
              Sending Request...
            </> : "Get My Free Quote"}
        </Button>
      </div>
    </Form>;
}
export default function HeroSection() {
  const formFooterItems = [{
    Icon: ShieldCheck,
    text: "100% Free"
  }, {
    Icon: Clock,
    text: "Fast Response"
  }, {
    Icon: Lock,
    text: "Private & Secure"
  }];
  return <section id="hero-section" className="relative isolate overflow-hidden" style={{
    background: NAVY,
    minHeight: "100vh",
    paddingTop: "120px",
    paddingBottom: "80px",
    fontFamily: FONT_BODY
  }}>
      {/* background photo */}
      <picture>
        <source media="(max-width: 640px)" srcSet={HERO_IMG.mobile} />
        <source media="(max-width: 1024px)" srcSet={HERO_IMG.tablet} />
        <img src={HERO_IMG.desktop} alt="Gold Garage Door technician repairing a residential garage door in Sydney" loading="eager" decoding="async" style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        objectFit: "cover",
        objectPosition: "65% center",
        opacity: 0.5,
        zIndex: 0
      }} />
      </picture>

      {/* soft navy overlay on top of the image */}
      <div style={{
      position: "absolute",
      inset: 0,
      background: `linear-gradient(135deg, ${NAVY} 0%, rgba(8,20,56,0.55) 50%, rgba(8,20,56,0.7) 100%)`,
      zIndex: 1
    }} />

      {/* grain */}
      <div aria-hidden style={{
      position: "absolute",
      inset: 0,
      opacity: 0.04,
      mixBlendMode: "overlay",
      zIndex: 2,
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E\")",
      pointerEvents: "none"
    }} />

      {/* gold scanline accent */}
      <div style={{
      position: "absolute",
      top: 120,
      left: 0,
      width: 4,
      height: 80,
      background: GOLD,
      zIndex: 3
    }} />

      <div style={{
      position: "relative",
      zIndex: 4,
      maxWidth: 1280,
      margin: "0 auto",
      padding: "0 24px",
      display: "grid",
      gridTemplateColumns: "minmax(0, 1.1fr) minmax(0, 0.9fr)",
      gap: 56,
      alignItems: "center"
    }} className="home-hero-grid">
        {/* left column */}
        <div>
          <div style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 10,
          padding: "8px 16px",
          background: "rgba(245,166,35,0.1)",
          border: "1px solid rgba(245,166,35,0.3)",
          borderRadius: 100,
          marginBottom: 28
        }}>
            <span className="relative flex size-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-lg bg-[#34A853] opacity-75" />
              <span className="relative inline-flex rounded-lg size-2 bg-[#34A853]" />
            </span>
            <span style={{
            fontFamily: FONT_BODY,
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.15em",
            textTransform: "uppercase",
            color: GOLD
          }}>
              Fast Same-Day Service · Sydney
            </span>
          </div>

          <h1 style={{
          fontFamily: FONT_HEAD,
          fontSize: "clamp(36px, 5.5vw, 64px)",
          fontWeight: 800,
          color: "#ffffff",
          lineHeight: 1.04,
          letterSpacing: "-0.025em",
          margin: "0 0 24px"
        }}>
            Sydney Garage Door Repair{" "}
            <span style={{
            background: `linear-gradient(135deg, ${GOLD} 0%, #ffc142 100%)`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            display: "inline-block"
          }}>
              Fast Same-Day Service
            </span>
          </h1>

          <div style={{
          width: 46,
          height: 2,
          background: GOLD,
          margin: "0 0 24px"
        }} />

          <p style={{
          fontFamily: FONT_BODY,
          fontSize: "clamp(16px, 1.4vw, 19px)",
          color: "rgba(255,255,255,0.78)",
          lineHeight: 1.6,
          maxWidth: 540,
          margin: "0 0 36px"
        }}>
            Expert repair for springs, motors, and cables. We service all major
            brands with honest upfront pricing and same-day emergency support
            across Sydney.
          </p>

          <div style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 24,
          marginBottom: 40
        }}>
            {[{
            Icon: CheckCircle2,
            text: "Fully Insured Technicians"
          }, {
            Icon: ShieldCheck,
            text: "Warranty-Backed Work"
          }, {
            Icon: Star,
            text: "5.0  Google rated"
          }].map(({
            Icon,
            text
          }, i) => <div key={i} style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            color: "rgba(255,255,255,0.85)",
            fontSize: 14,
            fontWeight: 500,
            fontFamily: FONT_BODY
          }}>
                <Icon size={18} color={GOLD} strokeWidth={2} />
                {text}
              </div>)}
          </div>

          <div style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 14
        }}>
            <Link to="tel:0485000933" aria-label="Call Gold Garage Door on 0485 000 933" style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            padding: "16px 28px",
            background: GOLD,
            color: NAVY,
            fontFamily: FONT_HEAD,
            fontSize: 16,
            fontWeight: 700,
            borderRadius: 12,
            textDecoration: "none",
            transition: "transform 0.2s, box-shadow 0.2s",
            boxShadow: "0 8px 24px rgba(245,166,35,0.3)"
          }} onMouseEnter={e => {
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow = "0 12px 32px rgba(245,166,35,0.45)";
          }} onMouseLeave={e => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 8px 24px rgba(245,166,35,0.3)";
          }}>
              <Phone size={18} strokeWidth={2.5} />
              0485 000 933
            </Link>
            <Link to="#contact-us" style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            padding: "16px 28px",
            background: "transparent",
            color: "#ffffff",
            fontFamily: FONT_HEAD,
            fontSize: 16,
            fontWeight: 600,
            borderRadius: 12,
            textDecoration: "none",
            border: "1px solid rgba(255,255,255,0.25)",
            transition: "background 0.2s, border-color 0.2s"
          }} onMouseEnter={e => {
            (e.currentTarget as HTMLAnchorElement).style.background = "rgba(255,255,255,0.08)";
            (e.currentTarget as HTMLAnchorElement).style.borderColor = "rgba(255,255,255,0.4)";
          }} onMouseLeave={e => {
            (e.currentTarget as HTMLAnchorElement).style.background = "transparent";
            (e.currentTarget as HTMLAnchorElement).style.borderColor = "rgba(255,255,255,0.25)";
          }}>
              Get Free Quote
              <ArrowRight size={16} strokeWidth={2.5} />
            </Link>
          </div>
        </div>

        {/* right column - quote card */}
        <div id="contact-us" className="relative bg-white backdrop-blur-sm p-6 md:p-8 rounded-lg border border-[#F5A623]/40 shadow-2xl shadow-[#081438]/20 overflow-hidden scroll-mt-28">
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#F5A623] via-[#ffc142] to-[#F5A623]" />

          <div className="text-center mb-6">
            <h2 className="text-xl font-bold text-[#081438] mb-1.5">Get Your Free Quote</h2>
            <p className="text-[13.5px] text-[#555] font-medium">Fast response · No obligation · No hidden fees</p>
            <div className="h-px bg-gradient-to-r from-transparent via-[#F5A623]/20 to-transparent my-4" />
          </div>

          <FormProvider formId="hero_quote_form" sectionName="Hero Section" defaultValues={{
          name: "",
          phone: "",
          suburb: "",
          service: ""
        }} successMessage="Thank you! Your quote request has been sent.">
            <HeroQuoteForm />
          </FormProvider>

          <div className="mt-6 pt-4 border-t border-black/5 flex justify-center gap-5">
            {formFooterItems.map(({
            Icon,
            text
          }, i) => <div key={i} data-index={i} className="flex items-center gap-2">
                <div className="flex items-center justify-center size-7 rounded-lg bg-[#F5A623]/10 shrink-0">
                  <Icon className="size-[14px] text-[#F5A623]" aria-hidden="true" />
                </div>
                <span className="text-[12px] font-semibold text-[#333]">{text}</span>
              </div>)}
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 960px) {
          .home-hero-grid {
            grid-template-columns: 1fr !important;
            gap: 40px !important;
          }
        }
      `}</style>
    </section>;
}